import _ from 'underscore';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { Skeleton, SkeletonItem, Text } from '@fluentui/react-components';
import { useFetch } from 'use-http';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { LINKEDIN_WIZARD_STEP_PATHS, isAudienceTooNarrow } from '@bingads-webui-campaign-react/linkedin-utils';
import { getIsStepValid, getStepData, useWizardService, WIZARD_EVENTS } from '@bingads-webui-fluent-component/wizard';
import { useSummaryPanelStyles } from '../styles/summary-panel-styles';

const DEBOUNCE_TIME = 500;

const AgeTargetMap = {
  EighteenToTwentyFive: 'EighteenToTwentyFour',
  TwentyFiveToThirtyFive: 'TwentyFiveToThirtyFour',
  ThirtyFiveToFiftyFive: 'ThirtyFiveToFiftyFour',
  FiftyFiveAndAbove: 'FifityFiveAndAbove',
};

const formatPayload = (targetingData) => {
  const {
    defaultLocation,
    locations,
    audiences: {
      companyNames,
      companyIndustries,
      jobSeniorities,
      jobFunctions,
      jobTitles,
    },
    genders,
    ageRanges,
  } = targetingData;

  return {
    Location: {
      Includes: !_.isEmpty(locations.positiveLocationTargets)
        ? _.map(locations.positiveLocationTargets, loc => loc.location.Id)
        : [defaultLocation.Id],
    },
    Age: { Includes: _.compact(_.map(ageRanges, ageRange => AgeTargetMap[ageRange])) },
    Gender: { Includes: genders },
    Company: { Includes: _.pluck(companyNames, 'id') },
    Industry: { Includes: _.pluck(companyIndustries, 'id') },
    JobSeniority: { Includes: _.pluck(jobSeniorities, 'id') },
    JobFunction: { Includes: _.pluck(jobFunctions, 'id') },
    JobTitle: { Includes: _.pluck(jobTitles, 'id') },
  };
};

const getAudienceSizeFromData = data => _.get(data, 'elements[0].total', 0);

const usePerformanceMetrics = ({ onUpdate }) => {
  const { scope: { values: { aid } } } = useContext(ScopeContext);
  const {
    post, data, error, loading,
  } = useFetch(`Targeting/${aid}/LinkedInTargeting/AudienceEstimation`, {
    loading: true,
    onNewData: (_oldData, newData) => {
      onUpdate(getAudienceSizeFromData(newData));

      return newData;
    },
    onError: () => onUpdate(null),
  });

  const debouncedPost = useMemo(() => _.debounce(post, DEBOUNCE_TIME), [post]);
  const audienceSize = useMemo(() => getAudienceSizeFromData(data), [data]);

  return {
    fetchEstimations: debouncedPost,
    data: { audienceSize },
    error: !!error,
    loading,
  };
};

export const SummaryPanelView = () => {
  const i18n = useContext(I18nContext);
  const classes = useSummaryPanelStyles();

  const { state, send } = useWizardService();

  const currentStepData = getStepData(state, LINKEDIN_WIZARD_STEP_PATHS.TARGETING);
  const isCurrentStepValid = getIsStepValid(state, LINKEDIN_WIZARD_STEP_PATHS.TARGETING);

  const updateAudienceSize = useCallback((audienceSize) => {
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { audienceSize });
  }, [send]);

  const {
    fetchEstimations, data, error, loading,
  } = usePerformanceMetrics({
    onUpdate: updateAudienceSize,
  });

  useEffect(() => {
    if (_.isEmpty(currentStepData) || !isCurrentStepValid) { return; }

    const formattedPayload = formatPayload(currentStepData);

    fetchEstimations(formattedPayload);
  }, [fetchEstimations, currentStepData, isCurrentStepValid]);

  const dataToDisplay = useMemo(() => {
    if (error) {
      return i18n.getString(_TL_('No data available'));
    }

    if (isAudienceTooNarrow(data.audienceSize)) {
      return i18n.getString(_TL_('Audience too narrow'));
    }

    return `${(new Intl.NumberFormat()).format(data.audienceSize)}+`;
  }, [i18n, data, error]);

  return (
    <div className="summary-panel-content">
      <Text className={classes.label} block>
        {i18n.getString(_TL_('Target audience size'))}
      </Text>
      {loading && (
        <Skeleton appearance="translucent">
          <div className={classes.shimmer}>
            <SkeletonItem size="32" />
          </div>
        </Skeleton>
      )}
      {!loading && (
        <>
          <Text className={classes.estimations} block>
            {dataToDisplay}
          </Text>
          {isAudienceTooNarrow(data.audienceSize) && (
            <Text className={classes.error} block>
              {i18n.getString(_TL_('Oops! Your audience is too small to create a campaign. Please expand your audience.'))}
            </Text>
          )}
        </>
      )}
    </div>
  );
};
